import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PackageOpen } from 'lucide-react';
import type { Plan } from '../types/admin.types';

/**
 * El selector de plan, con lo que hacía falta cuando no hay ninguno.
 *
 * 🔴 Qué problema resuelve, y por qué costó tiempo real.
 *
 * Las dos pantallas que asignan plan hacían `plans.map(...)` y nada más. Con la tabla
 * de planes vacía el desplegable se abre y **no muestra nada**: ni una fila, ni un
 * texto, ni un error. No parece "falta cargar planes", parece que el sistema está
 * roto. Y era exactamente el caso de producción, donde el `INSERT` de planes de la
 * migración de abril nunca corrió.
 *
 * Un desplegable vacío es el peor estado posible de una interfaz: no dice qué pasó ni
 * qué hacer. Así que cuando no hay planes no se muestra un desplegable — se dice qué
 * falta y se ofrece el camino para arreglarlo.
 *
 * Se comparte entre el listado de empresas y el detalle de una empresa porque las dos
 * tenían el mismo bloque copiado. Un solo lugar, un solo comportamiento.
 */
export function SelectorDePlan({
  planes: todos,
  valor,
  onCambio,
}: {
  planes: Plan[];
  valor: string;
  onCambio: (id: string) => void;
}) {
  const navigate = useNavigate();
  // El plan DEMO es de las empresas demo (lo edita Planes): no se ofrece al dar de alta ni al asignar.
  const planes = todos.filter((p) => (p.tier as string) !== 'DEMO');

  if (planes.length === 0) {
    return (
      <div className="space-y-3 rounded-md border border-dashed p-4">
        <div className="flex items-start gap-2">
          <PackageOpen
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <div className="space-y-1">
            <p className="text-sm font-medium">Todavía no hay ningún plan cargado</p>
            <p className="text-sm text-muted-foreground">
              Los planes son de toda la plataforma, no de esta empresa. Hay que crear al menos uno
              antes de poder asignarlo.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/plataforma/planes')}>
          Ir a crear un plan
        </Button>
      </div>
    );
  }

  return (
    <Select value={valor} onValueChange={onCambio}>
      <SelectTrigger>
        <SelectValue placeholder="Seleccionar plan" />
      </SelectTrigger>
      <SelectContent>
        {planes.map((plan) => (
          <SelectItem key={plan.id} value={plan.id}>
            {plan.name} — ${plan.monthlyPrice}/mes
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
